import { idbGet, idbPut, STORES } from './idb';

// تسجيل الدخول دون اتصال: بعد كل دخول ناجح مع الاتصال نحفظ على الجهاز
// - بصمة كلمة المرور (PBKDF2 بملح عشوائي)، لا كلمة المرور نفسها
// - جلسة كل مزاد (التوكن وبيانات المستخدم) مشفّرة بمفتاح مشتق من كلمة المرور (AES-GCM)
// فلا يمكن قراءة الجلسة من الجهاز دون كلمة المرور الصحيحة.
const ITERATIONS = 150000;
const KEY_PREFIX = 'cred:';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function cryptoAvailable() {
  return typeof crypto !== 'undefined' && Boolean(crypto.subtle);
}

function normalizeUsername(username) {
  return String(username || '').trim().toLowerCase();
}

function toBase64(bytes) {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)));
}

function fromBase64(text) {
  return Uint8Array.from(atob(text), (char) => char.charCodeAt(0));
}

function sameBytes(left, right) {
  if (left.length !== right.length) return false;
  let diff = 0;
  for (let i = 0; i < left.length; i += 1) diff |= left[i] ^ right[i];
  return diff === 0;
}

// 512 بت من كلمة المرور: النصف الأول بصمة للتحقق، والثاني مفتاح التشفير.
async function deriveSecrets(password, salt, iterations) {
  const material = await crypto.subtle.importKey('raw', encoder.encode(String(password)), 'PBKDF2', false, ['deriveBits']);
  const bits = new Uint8Array(await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    material,
    512,
  ));
  const key = await crypto.subtle.importKey('raw', bits.slice(32), 'AES-GCM', false, ['encrypt', 'decrypt']);
  return { verifier: bits.slice(0, 32), key };
}

async function encryptJson(key, value) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoder.encode(JSON.stringify(value)));
  return { iv: toBase64(iv), data: toBase64(data) };
}

async function decryptJson(key, box) {
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64(box.iv) }, key, fromBase64(box.data));
  return JSON.parse(decoder.decode(plain));
}

async function readRecord(username) {
  try {
    return await idbGet(STORES.kv, `${KEY_PREFIX}${normalizeUsername(username)}`);
  } catch {
    return null;
  }
}

// يُستدعى بعد كل دخول ناجح مع الاتصال. تغيّر كلمة المرور يعيد إنشاء السجل من جديد.
export async function rememberLogin({ username, password, token, user }) {
  if (!cryptoAvailable() || !username || !password || !token || !user) return;
  const auctionId = Number(user.selected_auction_id || 0) || 0;

  try {
    let record = await readRecord(username);
    let secrets = null;

    if (record) {
      secrets = await deriveSecrets(password, fromBase64(record.salt), record.iterations);
      if (!sameBytes(secrets.verifier, fromBase64(record.verifier))) record = null;
    }

    if (!record) {
      const salt = crypto.getRandomValues(new Uint8Array(16));
      secrets = await deriveSecrets(password, salt, ITERATIONS);
      record = {
        salt: toBase64(salt),
        iterations: ITERATIONS,
        verifier: toBase64(secrets.verifier),
        sessions: {},
        auctions: [],
      };
    }

    record.sessions[auctionId] = await encryptJson(secrets.key, { token, user });
    const auction = (user.auctions || []).find((entry) => Number(entry.id) === auctionId);
    record.auctions = [
      ...record.auctions.filter((entry) => Number(entry.id) !== auctionId),
      { id: auctionId, name: auction?.name || user.auction?.name || '' },
    ];
    record.updated_at = new Date().toISOString();

    await idbPut(STORES.kv, record, `${KEY_PREFIX}${normalizeUsername(username)}`);
  } catch {
    // فشل الحفظ لا يمنع الدخول مع الاتصال.
  }
}

// المزادات التي دخلها المستخدم من قبل على هذا الجهاز، لقائمة اختيار المزاد دون اتصال.
export async function rememberedAuctions(username) {
  const record = await readRecord(username);
  return (record?.auctions || []).filter((entry) => entry.id);
}

// يعيد { token, user } إن طابقت كلمة المرور، أو يرمي خطأ برسالة واضحة.
export async function loginOffline(username, password, auctionId = null) {
  if (!cryptoAvailable()) {
    throw new Error('تسجيل الدخول دون اتصال غير متاح على هذا الجهاز.');
  }

  const record = await readRecord(username);
  if (!record) {
    throw new Error('لا يوجد اتصال، ولم يسجّل هذا المستخدم دخوله على هذا الجهاز من قبل. سجّل الدخول مرة مع الاتصال أولًا.');
  }

  const secrets = await deriveSecrets(password, fromBase64(record.salt), record.iterations);
  if (!sameBytes(secrets.verifier, fromBase64(record.verifier))) {
    const error = new Error('بيانات الدخول غير صحيحة');
    error.invalidCredentials = true;
    throw error;
  }

  const ids = Object.keys(record.sessions || {});
  const chosen = auctionId ? String(Number(auctionId)) : (ids.length === 1 ? ids[0] : null);
  if (!chosen || !record.sessions[chosen]) {
    throw new Error(ids.length > 1
      ? 'اختر المزاد. دون اتصال تظهر فقط المزادات التي دخلتها سابقًا على هذا الجهاز.'
      : 'لم تسجّل الدخول على هذا المزاد من هذا الجهاز مع الاتصال من قبل.');
  }

  return decryptJson(secrets.key, record.sessions[chosen]);
}
